import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { PageErrorBoundary } from './components/layout/PageErrorBoundary';
import { NavSection, Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { useHRStore } from './hooks/useHRStore';
import { Dashboard } from './pages/Dashboard';
import { EmployeeProfile } from './pages/EmployeeProfile';
import { Employees } from './pages/Employees';
import { PolicyDetail } from './pages/PolicyDetail';
import { PolicyEditor } from './pages/PolicyEditor';
import { Reports } from './pages/Reports';
import { Settings } from './pages/Settings';
import { TimeOff } from './pages/TimeOff';
import type { Route } from './types/hr';
import { nameById } from './utils/employees';
import { formatDays } from './utils/format';
import { getHRState, hrActions } from './utils/hrStore';
export function App() {
  const {
    requests
  } = useHRStore();
  const [route, setRoute] = useState<Route>({
    page: 'dashboard'
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useCallback((next: Route) => {
    setRoute(next);
    setMobileNavOpen(false);
    window.scrollTo({
      top: 0
    });
  }, []);
  const decideRequest = useCallback((id: string, status: 'approved' | 'denied') => {
    const request = getHRState().requests.find(r => r.id === id);
    hrActions.decideRequest(id, status);
    if (!request) return;
    const name = nameById(request.employeeId);
    if (status === 'approved') {
      toast.success(`Approved ${name}'s request`, {
        description: `${formatDays(request.days)} added to the Who's Out calendar.`
      });
    } else {
      toast(`Denied ${name}'s request`, {
        description: 'Removed from the queue. They will be notified by email.'
      });
    }
  }, []);
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  return <div className="min-h-screen w-full bg-canvas font-sans text-slate-700" data-id="element-0" data-mp="swqcvjfihlyi">
      <Sidebar active={sectionFor(route)} pendingCount={pendingCount} mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} onNavigate={section => navigate(SECTION_ROUTES[section])} data-id="element-1" data-mp="34x13f0jn52b" />
      <div className="lg:pl-72" data-id="element-2" data-mp="85l0anyxes7m">
        <TopBar onNavigate={navigate} onOpenMenu={() => setMobileNavOpen(true)} data-id="element-3" data-mp="86mev2f5hwu3" />
        <main className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10" data-id="element-4" data-mp="3gkv1zuywlvev">
          <AnimatePresence mode="wait" data-id="element-5" data-mp="3gj1qo2dcin0j">
            <motion.div key={routeKey(route)} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.18,
            ease: 'easeOut'
          }} data-id="element-6" data-mp="3bn30qacdcwy0">
              <PageErrorBoundary key={routeKey(route)} onReset={() => navigate({
              page: 'dashboard'
            })} data-id="element-7" data-mp="2xun35p28lr4h">
                {route.page === 'dashboard' && <Dashboard requests={requests} onDecide={decideRequest} onNavigate={navigate} data-id="element-8" data-mp="2suquvdmibdj4" />}
                {route.page === 'employees' && <Employees onNavigate={navigate} data-id="element-9" data-mp="18cgfzx81o9n8" />}
                {route.page === 'profile' && <EmployeeProfile employeeId={route.employeeId} initialTab={route.tab} requests={requests} onNavigate={navigate} data-id="element-10" data-mp="1d59hmc87v1v3" />}
                {route.page === 'timeoff' && <TimeOff tab={route.tab} requests={requests} onDecide={decideRequest} onNavigate={navigate} data-id="element-11" data-mp="13e55rldu7x4h" />}
                {route.page === 'policy' && <PolicyDetail policyId={route.policyId} onNavigate={navigate} data-id="element-12" data-mp="1r4sfmc7bcwto" />}
                {route.page === 'policy-edit' && <PolicyEditor policyId={route.policyId} onNavigate={navigate} data-id="element-13" data-mp="1w2khnz1o9zbl" />}
                {route.page === 'reports' && <Reports data-id="element-14" data-mp="1i5nh35vr53hi" />}
                {route.page === 'settings' && <Settings onNavigate={navigate} data-id="element-15" data-mp="1m44rpzedsw3o" />}
              </PageErrorBoundary>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      <Toaster position="bottom-right" toastOptions={{
      style: {
        fontFamily: 'Poppins, sans-serif',
        borderRadius: '14px'
      }
    }} data-id="element-16" data-mp="aq0ywwjj5e07" />
    </div>;
}
const SECTION_ROUTES: Record<NavSection, Route> = {
  dashboard: {
    page: 'dashboard'
  },
  employees: {
    page: 'employees'
  },
  timeoff: {
    page: 'timeoff',
    tab: 'whos-out'
  },
  reports: {
    page: 'reports'
  },
  settings: {
    page: 'settings'
  }
};
function sectionFor(route: Route): NavSection {
  switch (route.page) {
    case 'profile':
      return 'employees';
    case 'policy':
    case 'policy-edit':
      return 'timeoff';
    default:
      return route.page;
  }
}
function routeKey(route: Route): string {
  switch (route.page) {
    case 'profile':
      return `profile-${route.employeeId}`;
    case 'timeoff':
      return 'timeoff';
    case 'policy':
      return `policy-${route.policyId}`;
    case 'policy-edit':
      return `policy-edit-${route.policyId ?? 'new'}`;
    default:
      return route.page;
  }
}